import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, Upload, History, FileJson, FileSpreadsheet, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const fmtSize = (n) =>
  (n || 0) >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max((n || 0) / 1024, 0.1).toFixed(1)} KB`;
const fmtDate = (iso) => new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });

export default function BackupRestore() {
  const [history, setHistory] = useState([]);
  const [format, setFormat] = useState("json");
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const fileRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/backup/history");
      setHistory(data);
    } catch { /* riwayat kosong */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/backup/download?format=${format}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const fname = (cd.match(/filename="?([^";]+)"?/) || [])[1] || `veloce-backup.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup ${format.toUpperCase()} berhasil diunduh`);
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDownloading(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm(`Restore dari "${file.name}" akan MENIMPA data bisnis Anda saat ini dengan isi backup. Lanjutkan?`)) return;
    setRestoring(true);
    try {
      const isSql = file.name.toLowerCase().endsWith(".sql");
      const { data } = await api.post("/backup/restore", file,
        { headers: { "Content-Type": isSql ? "application/sql" : "application/json" } });
      const r = data.restored || {};
      toast.success(`Restore berhasil: ${r.businesses} bisnis, ${r.products} produk, ${r.leads} leads`);
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-card p-6 space-y-3">
          <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
            <Download className="w-5 h-5 text-accent" />
          </div>
          <h3 className="font-display font-semibold">Backup Data Internal</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Unduh seluruh data internal bisnis Anda — produk, promo, pesanan, leads, notifikasi,
            statistik, riwayat chat, dan konfigurasi bisnis.
          </p>
          <div className="flex gap-1.5" data-testid="backup-format-picker">
            {[
              { v: "json", label: "JSON", icon: FileJson },
              { v: "sql", label: "SQL", icon: FileSpreadsheet },
            ].map((f) => (
              <button key={f.v} type="button" data-testid={`backup-format-${f.v}`}
                onClick={() => setFormat(f.v)}
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                  format === f.v ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:bg-secondary"
                }`}>
                <f.icon className="w-3.5 h-3.5" /> {f.label}
              </button>
            ))}
          </div>
          <Button data-testid="btn-download-backup" onClick={download} disabled={downloading}
            className="bg-accent hover:bg-accent/90 text-white">
            {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            {downloading ? "Menyiapkan..." : `Unduh Backup (${format.toUpperCase()})`}
          </Button>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 space-y-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Upload className="w-5 h-5 text-amber-600" />
          </div>
          <h3 className="font-display font-semibold">Restore dari Backup</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Pulihkan data dari file backup <b>JSON</b> atau <b>SQL</b> yang dihasilkan aplikasi ini.
            Data bisnis yang ID-nya sama akan <b>ditimpa</b> dengan isi backup.
          </p>
          <input ref={fileRef} type="file" accept=".json,.sql,application/json" className="hidden"
            data-testid="input-restore-file" onChange={onFile} />
          <Button data-testid="btn-restore-upload" variant="outline" disabled={restoring}
            onClick={() => fileRef.current?.click()}>
            {restoring ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
            {restoring ? "Memulihkan..." : "Upload File Backup"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display font-semibold flex items-center gap-2 mb-4">
          <History className="w-4 h-4" /> Riwayat Backup Terakhir
        </h3>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="backup-history-empty">
            Belum ada backup. Klik "Unduh Backup" untuk membuat backup pertama.
          </p>
        ) : (
          <div className="space-y-2" data-testid="backup-history-list">
            {history.map((h) => (
              <div key={h.id} data-testid="backup-history-item"
                className="flex items-center gap-3 rounded-xl border border-border px-4 py-3">
                {(h.format || "json") === "sql"
                  ? <FileSpreadsheet className="w-4 h-4 text-muted-foreground shrink-0" />
                  : <FileJson className="w-4 h-4 text-muted-foreground shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{fmtDate(h.created_at)}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {(h.counts?.businesses ?? 0)} bisnis · {(h.counts?.products ?? 0)} produk · {(h.counts?.leads ?? 0)} leads · oleh {h.admin_email}
                  </p>
                </div>
                <span className="text-[10px] font-mono uppercase rounded bg-muted px-1.5 py-0.5 text-muted-foreground shrink-0">
                  {h.format || "json"}
                </span>
                <Badge variant="outline" className={h.kind === "restore"
                  ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                  : "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"}>
                  {h.kind === "restore" ? "Restore" : "Backup"}
                </Badge>
                <span className="text-xs font-mono text-muted-foreground shrink-0">{fmtSize(h.size_bytes)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed flex items-start gap-1.5">
        <DatabaseBackup className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Backup hanya mencakup data internal aplikasi (MongoDB). File SQL dibuat kompatibel MySQL
        (tabel per koleksi, nilai kompleks disimpan sebagai JSON). Restore file SQL hanya didukung
        untuk file yang dihasilkan aplikasi ini. Password koneksi database ikut terbackup dalam
        bentuk terenkripsi — restore memerlukan kunci enkripsi yang sama.
      </p>
    </div>
  );
}
